import { Cell, Pie, PieChart } from 'recharts';
import { ChartContainer, ChartTooltip } from '../tailgrids/core/chart.js';
import { type ChartTone, TONE_COLOR, TONE_SWATCH } from './chart-colors.js';
import { ChartTooltipCard } from './ChartTooltipCard.js';
import { cn } from '../../utils/cn.js';

export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  tone: ChartTone;
}

interface DonutChartProps {
  segments: readonly DonutSegment[];
  /** Accessible name, e.g. "Trade outcomes". */
  title: string;
  /** The ring's centre: the headline number (e.g. "42"). */
  center: string;
  /** Under the centre figure (e.g. "trades"). */
  centerCaption?: string;
  size?: number;
  formatValue?: (value: number) => string;
}

/**
 * Shares of a whole as a ring with a centre figure and a legend centred below. Template example: NextAdmin
 * `charts/pie-charts/_components/chart-one` (centre value `text-[20px] font-semibold`, caption `text-sm
 * text-text-tertiary`). A zero total draws an empty ring.
 */
export function DonutChart({ segments, title, center, centerCaption, size = 160, formatValue }: DonutChartProps) {
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  const ring =
    total > 0
      ? segments.filter((s) => s.value > 0)
      : [{ key: 'empty', label: 'None', value: 1, tone: 'muted' as const }];
  const format = (value: number) => (formatValue ? formatValue(value) : value.toLocaleString());

  return (
    <figure
      className="m-0 flex flex-col items-center gap-4"
      aria-label={`${title}: ${segments.map((s) => `${s.label} ${format(s.value)}`).join(', ')}`}
      role="img"
    >
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <ChartContainer width={size} height={size}>
          <PieChart>
            <Pie
              data={ring}
              dataKey="value"
              nameKey="label"
              innerRadius="68%"
              outerRadius="100%"
              paddingAngle={total > 0 && ring.length > 1 ? 2 : 0}
              stroke="none"
              isAnimationActive={false}
            >
              {ring.map((s) => (
                <Cell key={s.key} fill={total > 0 ? TONE_COLOR[s.tone] : 'var(--color-surface-alt)'} />
              ))}
            </Pie>
            {total > 0 && (
              <ChartTooltip
                content={(props) => (
                  <ChartTooltipCard
                    {...props}
                    label={undefined}
                    series={segments.map((s) => ({ key: s.label, label: s.label, tone: s.tone }))}
                    formatValue={formatValue}
                  />
                )}
              />
            )}
          </PieChart>
        </ChartContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <span className="text-[20px] leading-6 font-semibold tracking-[-0.2px] text-text-primary">{center}</span>
          {centerCaption && (
            <span className="text-sm font-normal tracking-[-0.15px] text-text-tertiary">{centerCaption}</span>
          )}
        </div>
      </div>
      <ul className="flex w-full flex-wrap justify-center gap-x-5 gap-y-2 pt-4 text-sm">
        {segments.map((s) => (
          <li key={s.key} className="flex items-center gap-2">
            <span className={cn('size-2 shrink-0 rounded-xs', TONE_SWATCH[s.tone])} aria-hidden="true" />
            <span className="text-text-secondary">{s.label}</span>
            <span className="font-semibold text-text-primary tabular-nums">{format(s.value)}</span>
          </li>
        ))}
      </ul>
    </figure>
  );
}
